import { Injectable } from '@angular/core';
import { AppCookieService } from '../../../shared/services/app-cookie.service';
import { CalculationCacheModel } from '../../models/calculation-cache.model';
import { CryptoService } from '../../../shared/services/crypto.service';
import { ProcessService } from '../process.service';
import { CalculationService } from './calculation.service';
import * as _ from 'lodash';
import { log } from '../../../shared/utils/console-logger';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import {ApplicationContextService} from './configuration/application-context.service';

@Injectable()
export class CalculationCacheService {

  public calculations: Array<CalculationCacheModel>;

  private readonly enableConsoleLogs = false;
  private readonly CALCULATIONS_LIMIT = 1;
  private readonly EXPIRES_MONTHS = 2;
  private readonly COOKIE_KEY = '_lc';

  get areCalculations(): boolean {
    return isNotNullOrUndefined(this.calculations) && this.calculations.length > 0;
  }

  get calculationsCacheEnabled(): boolean {
    return !this.contextService.calculationsCacheDisabled;
  }

  constructor(private cookiesService: AppCookieService,
              private cryptoService: CryptoService,
              private processService: ProcessService,
              private calculationService: CalculationService,
              private contextService: ApplicationContextService) {
  }

  public createCacheCalculation(): void {
    if (this.calculationsCacheEnabled) {
      this.prepareCalculation().then(calculation => {
        const cookieExpiresDate = this.createExpires();
        let calculations: CalculationCacheModel[] = _.cloneDeep(this.getAllCalculations());

        this.isCalculation(calculation.sessionId) ? calculations = this.updateCalculations(calculation) : calculations.push(calculation);
        this.sortCalculationsByDate(calculations);
        this.removeOldestCalculation(calculations);

        this.cookiesService.set(this.COOKIE_KEY, calculations, cookieExpiresDate);

        log({
          service: 'CalculationCacheService',
          method: 'createCacheCalculation: ',
          value: {'calculation': calculation, expiresDate: cookieExpiresDate}
        }, this.enableConsoleLogs);
      });
    }
  }

  public removeCalculationById(sessionId: string): void {
    const calculations = this.getAllCalculations().filter(calc => calc.sessionId !== sessionId);
    this.cookiesService.set(this.COOKIE_KEY, calculations);

    log({service: 'CalculationCacheService', method: 'removeCalculationById: ', value: calculations}, this.enableConsoleLogs);
  }

  public getAllCalculations(): Array<CalculationCacheModel> {
    const calculations = this.cookiesService.get(this.COOKIE_KEY);
    this.calculations = calculations ? calculations as Array<CalculationCacheModel> : new Array<CalculationCacheModel>()
    return this.calculations;
  }

  public validateCalculation(postalCode: string, pdId: string): Promise<boolean> {
    return this.cryptoService.compare(postalCode, pdId).then(resp => resp);
  }

  public removeCalculations(): void {
    this.cookiesService.set(this.COOKIE_KEY, null);
  }

  public isCalculation(sessionId: string): boolean {
    return isNotNullOrUndefined(this.getAllCalculations().find(calculation => calculation.sessionId === sessionId));
  }

  public calculationSectionVisible(sessionId: string): boolean {
    return this.areCalculations && !this.isCalculation(sessionId);
  }

  private calculationById(sessionId: string): CalculationCacheModel {
    return this.getAllCalculations().find(calculation => calculation.sessionId === sessionId);
  }

  private removeOldestCalculation(calculations: Array<CalculationCacheModel>): void {
    if (calculations.length > this.CALCULATIONS_LIMIT) {
      calculations.pop();

      log({service: 'CalculationCacheService', method: 'removeOldestCalculation: ', value: calculations}, this.enableConsoleLogs);
    }
  }

  private updateCalculations(calculation: CalculationCacheModel): Array<CalculationCacheModel> {
    return this.getAllCalculations().map((calc: CalculationCacheModel) => calc.sessionId === calculation.sessionId ? calculation : calc);
  }

  private sortCalculationsByDate(calculations: CalculationCacheModel[]): void {
    calculations.sort((a: CalculationCacheModel, b: CalculationCacheModel) => {
      return +new Date(a.creationDate) - +new Date(b.creationDate);
    }).reverse()

    log({service: 'CalculationCacheService', method: 'sortCalculationsByDate: ', value: calculations}, this.enableConsoleLogs);
  }

  private async prepareCalculation(): Promise<CalculationCacheModel> {
    const pcId = await this.cryptoService.encrypt(this.calculationService.calculationData.owner.address.postalCode).then(resp => resp);

    let calculation: CalculationCacheModel = {
      sessionId: this.processService.sessionId,
      offerId: this.processService.sessionSimpleId,
      creationDate: new Date(),
      vehicle: `${this.calculationService.vehicleData.brandName} - ${this.calculationService.vehicleData.modelName}`,
      registrationNumber: this.calculationService.vehicleData.registrationNumber,
      pcId: pcId
    };

    log({service: 'CalculationCacheService', method: 'prepareCalculation: ', value: calculation}, this.enableConsoleLogs);

    return calculation;
  }

  private createExpires(): Date {
    const date = new Date();
    date.setMonth(date.getMonth() + this.EXPIRES_MONTHS);

    return date;
  }

}
